/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useMemo, useState } from 'react'
import { ComposableMap, Geographies, Geography, ZoomableGroup } from 'react-simple-maps'
import { LanguageHelp } from '../../../helpers/LanguageHelper'
import { getCountryDisplayName, getLanguageDisplayName, normalizeCode } from '../../../helpers/GeoLanguageHelper'
import { resolveGeoCountryCode } from '../../../helpers/CountryIsoNumericMap'
import { Link } from 'react-router-dom'

const geoUrl = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json'
const DEFAULT_MAP_POSITION = { coordinates: [0, 20] as [number, number], zoom: 1 }

export class ChartsWidgetWorldMapDto {
  country = ''
  totalCount = 0
  positiveCount = 0
  negativeCount = 0
  neutralCount = 0
}

export class ChartsWidgetLanguageBarDto {
  language = ''
  totalCount = 0
  positiveCount = 0
  negativeCount = 0
  neutralCount = 0
}

type WorldMapProps = {
  className?: string
  data?: ChartsWidgetWorldMapDto[]
  mainLabel?: string
  sideLabel?: string
  height?: string
  warnings?: { url: string; message: string }[]
  onCountrySelect?: (countryCode: string) => void
}

type LanguageBarProps = {
  className?: string
  data?: ChartsWidgetLanguageBarDto[]
  mainLabel?: string
  sideLabel?: string
  height?: string
  warnings?: { url: string; message: string }[]
  onLanguageSelect?: (languageCode: string) => void
}

const countryCodeAlias: Record<string, string> = {
  UK: 'GB',
}

const normalizeMapCountryCode = (code?: string): string => {
  const normalized = normalizeCode(code)
  if (!normalized) return ''

  if (normalized.includes('-')) {
    const parts = normalized.split('-')
    return countryCodeAlias[parts[parts.length - 1]] || parts[parts.length - 1]
  }

  return countryCodeAlias[normalized] || normalized
}

const NO_DATA_COLOR = '#e9ecef'

const buildColorIntensity = (count: number, minCount: number, maxCount: number): number => {
  if (!count || count <= 0) return 0
  if (maxCount <= minCount) return 1

  // Sqrt scale: orta/düşük adetli ülkeler arasında kontrast artar (tek ülke baskın olsa bile)
  const scale = (value: number) => Math.sqrt(value)
  const normalized = (scale(count) - scale(minCount)) / (scale(maxCount) - scale(minCount))

  return Math.min(Math.max(normalized, 0), 1)
}

const getFillColor = (intensity: number): string => {
  if (intensity <= 0) return NO_DATA_COLOR

  const curved = Math.pow(intensity, 0.75)
  const lightness = 93 - curved * 60
  const saturation = 40 + curved * 55

  return `hsl(210, ${saturation}%, ${lightness}%)`
}

const getLegendGradient = (): string =>
  `linear-gradient(to right, ${NO_DATA_COLOR}, ${getFillColor(0.15)}, ${getFillColor(0.45)}, ${getFillColor(0.75)}, ${getFillColor(1)})`

const ChartsWidgetWorldMap: React.FC<WorldMapProps> = ({
  className = '',
  data = [],
  mainLabel = LanguageHelp('Country Distribution'),
  sideLabel = LanguageHelp('Drag to pan, scroll to zoom'),
  height = '360px',
  warnings = [],
  onCountrySelect,
}) => {
  const [tooltip, setTooltip] = useState<{ name: string; count: number; x: number; y: number } | null>(null)
  const [position, setPosition] = useState(DEFAULT_MAP_POSITION)

  const countByCountry = useMemo(() => {
    const map: Record<string, ChartsWidgetWorldMapDto> = {}
    data.forEach((item) => {
      const key = normalizeMapCountryCode(item.country)
      if (!key) return
      if (map[key]) {
        map[key] = {
          ...map[key],
          totalCount: map[key].totalCount + item.totalCount,
          positiveCount: map[key].positiveCount + item.positiveCount,
          negativeCount: map[key].negativeCount + item.negativeCount,
          neutralCount: map[key].neutralCount + item.neutralCount,
        }
      } else {
        map[key] = { ...item, country: key }
      }
    })
    return map
  }, [data])

  const colorScale = useMemo(() => {
    const counts = Object.values(countByCountry)
      .map((item) => item.totalCount)
      .filter((count) => count > 0)

    return {
      minCount: counts.length ? Math.min(...counts) : 0,
      maxCount: counts.length ? Math.max(...counts) : 1,
    }
  }, [countByCountry])

  const totalCount = useMemo(() => data.reduce((sum, item) => sum + item.totalCount, 0), [data])
  const hasData = totalCount > 0

  const handleZoomIn = () => {
    setPosition((prev) => ({ ...prev, zoom: Math.min(prev.zoom * 1.4, 8) }))
  }

  const handleZoomOut = () => {
    setPosition((prev) => ({ ...prev, zoom: Math.max(prev.zoom / 1.4, 1) }))
  }

  const handleResetView = () => {
    setPosition(DEFAULT_MAP_POSITION)
  }

  return (
    <div className={`card h-100 ${className}`}>
      <div className='card-header pt-5'>
        <h3 className='card-title d-flex flex-column'>
          <span className='card-label fw-bold text-dark'>{mainLabel}</span>
          <span className='text-muted fw-semibold fs-7'>{sideLabel}</span>
        </h3>
        {hasData && (
          <div className='card-toolbar gap-1'>
            <button type='button' className='btn btn-sm btn-icon btn-light' onClick={handleZoomOut} title={LanguageHelp('Zoom out')}>
              <span className='fs-4 lh-1'>−</span>
            </button>
            <button type='button' className='btn btn-sm btn-icon btn-light' onClick={handleZoomIn} title={LanguageHelp('Zoom in')}>
              <span className='fs-4 lh-1'>+</span>
            </button>
            <button type='button' className='btn btn-sm btn-light-primary' onClick={handleResetView}>
              {LanguageHelp('Reset view')}
            </button>
          </div>
        )}
      </div>
      <div className='card-body pt-2 pb-5 position-relative'>
        {hasData ? (
          <>
            <div style={{ width: '100%', height, overflow: 'hidden', cursor: 'grab' }}>
              <ComposableMap
                projection='geoEqualEarth'
                projectionConfig={{ scale: 130 }}
                width={800}
                height={380}
                style={{ width: '100%', height: '100%' }}
              >
                <ZoomableGroup
                  zoom={position.zoom}
                  center={position.coordinates}
                  minZoom={1}
                  maxZoom={8}
                  onMoveEnd={(nextPosition: { coordinates: [number, number]; zoom: number }) => {
                    setPosition({
                      coordinates: nextPosition.coordinates,
                      zoom: nextPosition.zoom,
                    })
                  }}
                >
                  <Geographies geography={geoUrl}>
                    {({ geographies }) =>
                      geographies.map((geo) => {
                        const countryCode = resolveGeoCountryCode(geo)
                        const countryData = countryCode ? countByCountry[countryCode] : undefined
                        const count = countryData?.totalCount ?? 0
                        const fillColor = getFillColor(buildColorIntensity(count, colorScale.minCount, colorScale.maxCount))

                        return (
                          <Geography
                            key={geo.rsmKey}
                            geography={geo}
                            style={{
                              default: { fill: fillColor, outline: 'none', stroke: '#ffffff', strokeWidth: 0.35 },
                              hover: {
                                fill: count > 0 ? '#1da1f2' : fillColor,
                                outline: 'none',
                                stroke: '#ffffff',
                                strokeWidth: 0.35,
                                cursor: count > 0 ? 'pointer' : 'default',
                              },
                              pressed: { fill: count > 0 ? '#0d8bd9' : fillColor, outline: 'none' },
                            }}
                            onMouseEnter={(event) => {
                              if (!countryCode) return
                              setTooltip({
                                name: getCountryDisplayName(countryCode),
                                count,
                                x: event.clientX,
                                y: event.clientY,
                              })
                            }}
                            onMouseMove={(event) => {
                              setTooltip((prev) =>
                                prev ? { ...prev, x: event.clientX, y: event.clientY } : prev
                              )
                            }}
                            onMouseLeave={() => setTooltip(null)}
                            onClick={() => {
                              if (count > 0 && onCountrySelect && countryCode) {
                                onCountrySelect(countryCode)
                              }
                            }}
                          />
                        )
                      })
                    }
                  </Geographies>
                </ZoomableGroup>
              </ComposableMap>
            </div>
            <div className='d-flex flex-wrap gap-4 mt-2 align-items-center'>
              <span className='text-muted fs-7'>{LanguageHelp('Total')}: {totalCount}</span>
              <div className='d-flex align-items-center gap-2'>
                <span className='text-muted fs-8'>{colorScale.minCount}</span>
                <div style={{ width: 120, height: 8, background: getLegendGradient(), borderRadius: 4 }} />
                <span className='text-muted fs-8'>{colorScale.maxCount}</span>
              </div>
            </div>
            {tooltip && (
              <div
                className='position-fixed bg-dark text-white px-3 py-2 rounded shadow-sm fs-7'
                style={{ top: tooltip.y + 12, left: tooltip.x + 12, zIndex: 2000, pointerEvents: 'none' }}
              >
                <div className='fw-bold'>{tooltip.name}</div>
                <div>{LanguageHelp('Total')}: {tooltip.count}</div>
              </div>
            )}
          </>
        ) : (
          <div className='d-flex flex-column align-items-center justify-content-center text-center' style={{ minHeight: height }}>
            <img alt='' src='/media/illustrations/sketchy-1/17.png' className='mw-200px mb-5' />
            {warnings.map((warning, key) => (
              <div key={key} className='text-muted fs-6 mb-2'>
                <Link to={warning.url}>{warning.message}</Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

const ChartsWidgetLanguageBar: React.FC<LanguageBarProps> = ({
  className = '',
  data = [],
  mainLabel = LanguageHelp('Language Distribution'),
  sideLabel = LanguageHelp('Click a language to filter'),
  height = '360px',
  warnings = [],
  onLanguageSelect,
}) => {
  const [searchText, setSearchText] = useState('')

  const chartData = useMemo(
    () =>
      [...data]
        .filter((item) => item.totalCount > 0)
        .sort((a, b) => b.totalCount - a.totalCount)
        .map((item) => ({
          ...item,
          label: getLanguageDisplayName(item.language),
        })),
    [data]
  )

  const filteredData = useMemo(() => {
    const query = searchText.trim().toLowerCase()
    if (!query) return chartData

    return chartData.filter(
      (item) =>
        item.label.toLowerCase().includes(query) ||
        item.language.toLowerCase().includes(query)
    )
  }, [chartData, searchText])

  const maxCount = useMemo(() => Math.max(...chartData.map((item) => item.totalCount), 1), [chartData])
  const totalCount = useMemo(() => chartData.reduce((sum, item) => sum + item.totalCount, 0), [chartData])
  const hasData = totalCount > 0
  const listHeight = `calc(${height} - 52px)`

  return (
    <div className={`card h-100 ${className}`}>
      <div className='card-header pt-5'>
        <h3 className='card-title d-flex flex-column'>
          <span className='card-label fw-bold text-dark'>{mainLabel}</span>
          <span className='text-muted fw-semibold fs-7'>{sideLabel}</span>
        </h3>
      </div>
      <div className='card-body pt-2 pb-5'>
        {hasData ? (
          <>
            <input
              type='text'
              className='form-control form-control-sm form-control-solid mb-4'
              placeholder={LanguageHelp('Search language')}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <div className='scroll-y pe-2' style={{ height: listHeight, maxHeight: listHeight, overflowY: 'auto' }}>
              {filteredData.length > 0 ? (
                filteredData.map((item) => {
                  const widthPercent = Math.max((item.totalCount / maxCount) * 100, item.totalCount > 0 ? 4 : 0)
                  const sharePercent = totalCount > 0 ? (item.totalCount / totalCount) * 100 : 0

                  return (
                    <button
                      key={item.language}
                      type='button'
                      className='btn btn-link text-start w-100 p-0 mb-3 text-decoration-none'
                      onClick={() => onLanguageSelect?.(item.language)}
                      title={item.label}
                    >
                      <div className='d-flex align-items-center justify-content-between gap-2 mb-1'>
                        <span className='text-gray-800 fs-7 fw-semibold text-truncate'>{item.label}</span>
                        <span className='text-muted fs-8 flex-shrink-0'>
                          {item.totalCount} ({sharePercent.toFixed(1)}%)
                        </span>
                      </div>
                      <div className='progress h-8px bg-light-primary'>
                        <div
                          className='progress-bar bg-primary'
                          role='progressbar'
                          style={{ width: `${widthPercent}%` }}
                          aria-valuenow={item.totalCount}
                          aria-valuemin={0}
                          aria-valuemax={maxCount}
                        />
                      </div>
                    </button>
                  )
                })
              ) : (
                <div className='text-center text-muted fs-7 py-10'>{LanguageHelp('No matching languages')}</div>
              )}
            </div>
            <div className='text-muted fs-7 mt-3'>
              {LanguageHelp('Total')}: {totalCount} · {chartData.length} {LanguageHelp('languages')}
            </div>
          </>
        ) : (
          <div className='d-flex flex-column align-items-center justify-content-center text-center' style={{ minHeight: height }}>
            <img alt='' src='/media/illustrations/sketchy-1/17.png' className='mw-200px mb-5' />
            {warnings.map((warning, key) => (
              <div key={key} className='text-muted fs-6 mb-2'>
                <Link to={warning.url}>{warning.message}</Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export { ChartsWidgetWorldMap, ChartsWidgetLanguageBar }
